// src/services/teamService.ts

import apiClient from "./apiClient";
import { TeamModel, CreateTeamRequest, UpdateTeamRequest } from "../models/TeamModel";
import { PageResponse } from "../models/PaginationModel";
import { CountryCountModel } from "../models/CountryCountModel";

const BASE_URL = "/api/teams";

/**
 * Team Service - manages sport-specific branches of clubs.
 */
export const teamService = {
  /**
   * Get all active teams (paginated).
   * Pass a 2-letter country code to restrict the list to teams whose parent
   * club belongs to that country (left-pane filter).
   */
  async getAllTeams(
    page = 0,
    size = 200,
    sortBy = "clubShortName",
    sortDir = "asc",
    country?: string | null,
    // Viewer preference. Forwarded to the API so the badge counts (and the
    // "most active" ordering) exclude rookie-authored entries when the viewer
    // has them hidden — matches getAllTopics/getAllMatches so the "Bugünkü
    // Girişler" mechanism is identical across every left-pane domain.
    showRookieEntries = true
  ): Promise<PageResponse<TeamModel>> {
    const params = new URLSearchParams({
      page:    String(page),
      size:    String(size),
      sortBy,
      sortDir,
      showRookieEntries: String(showRookieEntries),
    });
    if (country) params.set("country", country);
    return apiClient.get<PageResponse<TeamModel>>(`${BASE_URL}?${params.toString()}`);
  },

  /**
   * Distinct country codes (from the parent club) of active teams with
   * per-country counts — feeds the left-pane country filter dropdown.
   */
  async getTeamCountries(): Promise<CountryCountModel[]> {
    return apiClient.get<CountryCountModel[]>(`${BASE_URL}/countries`);
  },

  /**
   * Get a team by ID.
   */
  async getTeamById(id: number): Promise<TeamModel> {
    return apiClient.get<TeamModel>(`${BASE_URL}/${id}`);
  },

  /**
   * Get a team by club slug + sport/gender slug.
   * Example: getTeamBySlug("fenerbahce", "futbol")
   *          getTeamBySlug("galatasaray", "basketbol-kadin")
   */
  async getTeamBySlug(clubSlug: string, teamSlug: string): Promise<TeamModel> {
    return apiClient.get<TeamModel>(`${BASE_URL}/by-slug/${clubSlug}/${teamSlug}`);
  },

  /**
   * Fetch a team by its canonical single URL segment,
   * e.g. "galatasaray-futbol--372" (or a bare id, which entry references use).
   * Non-canonical segments make the backend answer 301 and apiClient raise
   * SlugRedirectError, which the page turns into a navigation.
   */
  async getTeamBySegment(teamSegment: string): Promise<TeamModel> {
    return apiClient.get<TeamModel>(`${BASE_URL}/by-slug/${teamSegment}`);
  },

  /**
   * Get all sport branches for a specific club.
   */
  async getTeamsByClub(clubId: number): Promise<TeamModel[]> {
    return apiClient.get<TeamModel[]>(`${BASE_URL}/by-club/${clubId}`);
  },

  /**
   * Search teams by club name or abbreviation.
   */
  async searchTeams(query: string): Promise<TeamModel[]> {
    return apiClient.get<TeamModel[]>(`${BASE_URL}/search?q=${encodeURIComponent(query)}`);
  },

  /**
   * Create a new team branch under an existing club (Admin only).
   */
  async createTeam(request: CreateTeamRequest): Promise<TeamModel> {
    return apiClient.post<TeamModel>(BASE_URL, request);
  },

  /**
   * Update a team branch - only active flag is mutable (Admin only).
   */
  async updateTeam(id: number, request: UpdateTeamRequest): Promise<TeamModel> {
    return apiClient.put<TeamModel>(`${BASE_URL}/${id}`, request);
  },

  /**
   * Soft-delete a team branch (Admin only).
   */
  async deleteTeam(id: number): Promise<void> {
    await apiClient.delete<void>(`${BASE_URL}/${id}`);
  },
};

export default teamService;